import { Avatar, Box, Button, Divider, Icon, IconButton, List, Typography } from '@shared/ui';
import type React from 'react';

type CalendarEvent = {
  id: string;
  summary?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
};

interface CalendarDetailProps {
  back: () => void;
  iconSrc?: string;
  isLoading: boolean;
  events: CalendarEvent[];
  onRefresh: () => void;
}

const CalendarDetail: React.FC<CalendarDetailProps> = ({
  back,
  iconSrc,
  isLoading,
  events,
  onRefresh,
}) => {
  const formatTime = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton size="small" onClick={back} aria-label="Back">
          <Icon>arrow_back</Icon>
        </IconButton>
        <Avatar
          variant="rounded"
          sx={{ width: 28, height: 28, ml: 0.5, mr: 1, borderRadius: 6, bgcolor: 'transparent' }}
          src={iconSrc}
        >
          <Icon sx={{ fontSize: 18 }}>calendar_today</Icon>
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Google Calendar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Moments will add meeting preps to selected events.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onRefresh} aria-label="Refresh">
          <Icon>refresh</Icon>
        </IconButton>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
          Meetings (today)
        </Typography>
        <Button size="small" variant="outlined">
          Add
        </Button>
      </Box>
      <List sx={{ p: 0 }}>
        {isLoading && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
        {!isLoading && events.length === 0 && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              No events found for today.
            </Typography>
          </Box>
        )}
        {events.map((ev) => {
          const sdt = ev.start?.dateTime;
          const edt = ev.end?.dateTime;
          const sDate = ev.start?.date;
          const eDate = ev.end?.date;
          const timeText =
            !sdt && (sDate || eDate)
              ? 'All day'
              : sdt && edt
                ? `${formatTime(sdt)} – ${formatTime(edt)}`
                : sdt
                  ? `${formatTime(sdt)}`
                  : '';
          return (
            <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75 }}>
              <Icon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }}>event</Icon>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {ev.summary || 'Untitled event'}
                </Typography>
                {timeText ? (
                  <Typography variant="caption" color="text.secondary">
                    {timeText}
                  </Typography>
                ) : null}
              </Box>
              <IconButton size="small" sx={{ ml: 0.5 }}>
                <Icon>more_horiz</Icon>
              </IconButton>
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default CalendarDetail;
